import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer } from "mobx-react";
import { SortType } from "../../../models/SortType";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import FormTextField from "../../components/common/form/FormTextField";
import AppButton from "../../components/common/buttons/AppButton";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import { GetVendorRequestsParams } from "../../../models/Vendors/Vendor";
import VendorRequestTable from "../../components/vendor/VendorRequestTable";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";
import VendorDetailsPopup from "./VendorDetailsPopup";
import AppDropDown from "../../components/common/filter/AppDropDown";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import { vendorDocumentsFilter, vendorStatusFilter, vendorSupplierTypeFilter, vendorTypeFilter } from "../../../models/Vendors/VendorRequestFilters";

const VendorRequestListing: React.FC = () => {
  const theme = useTheme();
  const { userStore, vendorStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const parseUrlParams = (): GetVendorRequestsParams => {
    return {
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sortBy: searchParams.get("sortBy") || "created_date",
      sortType:
        searchParams.get("sortType") === "ASC" ? SortType.ASC : SortType.DESC,
      search_text: searchParams.get("search_text") || "",
      missing_gst_doc: searchParams.get("missing_gst_doc") === "true" ? true : undefined,
      missing_pan_doc: searchParams.get("missing_pan_doc") === "true" ? true : undefined,
      missing_bank_details: searchParams.get("missing_bank_details") === "true" ? true : undefined,
      missing_product_catalog: searchParams.get("missing_product_catalog") === "true" ? true : undefined,
      missing_msme_doc: searchParams.get("missing_msme_doc") === "true" ? true : undefined,
      status: (() => {
        const statusParam = searchParams.get("status");
        const statusNum = statusParam ? Number(statusParam) : undefined;
        if (statusNum === 1 || statusNum === 2 || statusNum === 3) return statusNum;
        return undefined;
      })(),
      vendor_types: searchParams.get("vendor_types") || undefined,
      vendor_supplier_types: searchParams.get("vendor_supplier_types") || undefined,
    };
  };

  const initialFilters: GetVendorRequestsParams = parseUrlParams();

  const [filters, setFilters] =
    useState<GetVendorRequestsParams>(initialFilters);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);


  useEffect(() => {
    vendorStore.initializeSelectedFiltersFromUrl(searchParams);
  }, [location.search]);

  useEffect(() => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_VENDOR_REQUESTS])) {
      setIsLoading(true);
      vendorStore
        .getVendorRequest(filters)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "boolean") {
          if (value === true) {
            searchParams.set(key, "true");
          }
        } else if (key === "status" && typeof value === "number") {
          searchParams.set(key, value.toString());
        } else if (key === "vendor_types" && value && typeof value === "string") {
          searchParams.set(key, value);
        } else if (key === "vendor_supplier_types" && value && typeof value === "string") {
          searchParams.set(key, value);
        } else if (value !== undefined && value !== null && value !== "") {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value?.toString() || "";
          if (paramValue && paramValue !== "all") {
            searchParams.set(key, paramValue);
          }
        }
      });

      const queryString = searchParams.toString();
      navigate({ search: queryString }, { replace: true });
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  const handleApplyFilters = () => {
    const newFilters: GetVendorRequestsParams = {
      page_no: 1,
      limit: filters.limit || 30,
      sortBy: filters.sortBy || "created_date",
      sortType: filters.sortType || SortType.DESC,
      search_text: searchTerm.trim() || undefined,
    };

    if (vendorStore.selectedFilters.missingDocuments.includes("gst_certificate")) {
      newFilters.missing_gst_doc = true;
    }
    if (vendorStore.selectedFilters.missingDocuments.includes("pan_card")) {
      newFilters.missing_pan_doc = true;
    }
    if (vendorStore.selectedFilters.missingDocuments.includes("bank_details")) {
      newFilters.missing_bank_details = true;
    }
    if (vendorStore.selectedFilters.missingDocuments.includes("product_catalog")) {
      newFilters.missing_product_catalog = true;
    }
    if (vendorStore.selectedFilters.missingDocuments.includes("msme_certificate")) {
      newFilters.missing_msme_doc = true;
    }
    if (vendorStore.selectedFilters.status && vendorStore.selectedFilters.status !== "0") {
      const statusNum = Number(vendorStore.selectedFilters.status);
      if (!isNaN(statusNum) && (statusNum === 1 || statusNum === 2 || statusNum === 3)) {
        newFilters.status = statusNum;
      }
    }
    if (vendorStore.selectedFilters.vendorType && vendorStore.selectedFilters.vendorType.length > 0) {
      newFilters.vendor_types = vendorStore.selectedFilters.vendorType.join(",");
    }
    if (vendorStore.selectedFilters.vendorSupplierTypes && vendorStore.selectedFilters.vendorSupplierTypes !== "0") {
      newFilters.vendor_supplier_types = vendorStore.selectedFilters.vendorSupplierTypes;
    }

    setFilters(newFilters);
  };

  const resetFilters = () => {
    vendorStore.resetSelectedFilters();
    setSearchTerm("");
    const clearedFilters: GetVendorRequestsParams = {
      page_no: 1,
      limit: 30,
      sortBy: "created_date",
      sortType: SortType.DESC,
      search_text: undefined,
    };
    setFilters(clearedFilters);
  };

  const handleSearchBar = async (searchText: string | undefined) => {
    if (searchText && searchText.length !== 0) {
      setSearchTerm(searchText);
    } else {
      setSearchTerm("");
      setFilters((prev: GetVendorRequestsParams) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    const trimmedSearch = searchTerm.trim();
    setSearchTerm(trimmedSearch);
    setFilters((prev: GetVendorRequestsParams) => ({
      ...prev,
      search_text: trimmedSearch,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const trimmedSearch = searchTerm.trim();
      if (trimmedSearch.length > 0) {
        setSearchTerm(trimmedSearch);
        setFilters((prev: GetVendorRequestsParams) => ({
          ...prev,
          search_text: trimmedSearch,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setFilters((prev: GetVendorRequestsParams) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const resetSearch = () => {
    setSearchTerm("");
    setFilters((prev) => ({
      ...prev,
      search_text: "",
      page_no: 1,
    }));
  };

  const renderFilters = () => (
    <Box>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <AppDropDown
            title="Status"
            isMultiSelect={false}
            value={vendorStore.selectedFilters.status}
            list={vendorStatusFilter}
            fontSize={"12px"}
            onSingleSelectionChange={(id) => {
              vendorStore.setSelectedFilters({
                missingDocuments: vendorStore.selectedFilters.missingDocuments,
                status: id,
                vendorType: vendorStore.selectedFilters.vendorType,
                vendorSupplierTypes: vendorStore.selectedFilters.vendorSupplierTypes,
              });
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <AppSearchDropdownMulti
            title="Missing Documents"
            isMultiSelect={true}
            list={vendorDocumentsFilter}
            value={vendorStore.selectedFilters.missingDocuments.map((id) => {
              const item = vendorDocumentsFilter.find((doc) => doc.id === id);
              return item ? { id: item.id, value: item.value } : { id, value: id };
            })}
            local={true}
            fontSize={"12px"}
            handleSelect={(items) => {
              vendorStore.setSelectedFilters({
                missingDocuments: items.map((item) => item.id),
                status: vendorStore.selectedFilters.status,
                vendorType: vendorStore.selectedFilters.vendorType,
                vendorSupplierTypes: vendorStore.selectedFilters.vendorSupplierTypes,
              });
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <AppSearchDropdownMulti
            title="Type"
            isMultiSelect={true}
            list={vendorTypeFilter.filter((item) => item.id !== "0")}
            value={vendorStore.selectedFilters.vendorType.map((id) => {
              const item = vendorTypeFilter.find((type) => type.id === id);
              return item ? { id: item.id, value: item.value } : { id, value: id };
            })}
            local={true}
            fontSize={"12px"}
            handleSelect={(items) => {
              vendorStore.setSelectedFilters({
                missingDocuments: vendorStore.selectedFilters.missingDocuments,
                status: vendorStore.selectedFilters.status,
                vendorType: items.map((item) => item.id),
                vendorSupplierTypes: vendorStore.selectedFilters.vendorSupplierTypes,
              });
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <AppDropDown
            title="Supply Type"
            isMultiSelect={false}
            value={vendorStore.selectedFilters.vendorSupplierTypes}
            list={vendorSupplierTypeFilter}
            fontSize={"12px"}
            height="50px"
            onSingleSelectionChange={(id) => {
              vendorStore.setSelectedFilters({
                missingDocuments: vendorStore.selectedFilters.missingDocuments,
                status: vendorStore.selectedFilters.status,
                vendorType: vendorStore.selectedFilters.vendorType,
                vendorSupplierTypes: id,
              });
            }}
          />
        </Grid2>

      </Grid2>
    </Box>
  );

  const handleViewDetails = (vendorId: number) => {
    vendorStore.openViewDetailsPopup(vendorId);
  };

  const getSelectedVendor = () => {
    if (!vendorStore.viewDetailsPopup.vendorId) return null;
    return vendorStore.vendorRequests.find(
      (v) => v.id === vendorStore.viewDetailsPopup.vendorId
    ) || null;
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setFilters((prev: GetVendorRequestsParams) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev: GetVendorRequestsParams) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleFinanceApprove = (vendorId: number) => {
    if (
      userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_FINANCE])
    ) {
      vendorStore.openFinanceApprovePopup(vendorId);
    } else {
      showError("Error", "You are not authorized to approve vendor requests");
    }
  };

  const handleFinanceReject = (vendorId: number) => {
    if (
      userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_FINANCE])
    ) {
      vendorStore.openFinanceRejectPopup(vendorId);
    } else {
      showError("Error", "You are not authorized to reject vendor requests");
    }
  };

  const handleSourcingApprove = (vendorId: number) => {
    if (
      userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_SOURCING])
    ) {
      vendorStore.openSourcingApprovePopup(vendorId);
    } else {
      showError("Error", "You are not authorized to approve vendor requests");
    }
  };

  const handleSourcingReject = (vendorId: number) => {
    if (
      userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_SOURCING])
    ) {
      vendorStore.openSourcingRejectPopup(vendorId);
    } else {
      showError("Error", "You are not authorized to reject vendor requests");
    }
  };


  const handleFinanceApproveConfirm = async (vendorId: number) => {
    if (!userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_FINANCE])) {
      showError("Error", "You are not authorized to approve vendor requests");
      vendorStore.closeFinanceApprovePopup();
      return;
    }

    try {
      await vendorStore.approveFinanceVendorRequest(vendorId, userStore.user?.id || undefined, userStore.user?.name || undefined);
      showSuccess("Finance approval successful");
      vendorStore.closeFinanceApprovePopup();
      setIsLoading(true);
      vendorStore
        .getVendorRequest(filters)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
        "Something went wrong! Please try again later."
      );
      vendorStore.closeFinanceApprovePopup();
    }
  };

  const handleFinanceRejectConfirm = async (
    vendorId: number,
    rejectionReason?: string
  ) => {
    if (!userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_FINANCE])) {
      showError("Error", "You are not authorized to reject vendor requests");
      vendorStore.closeFinanceRejectPopup();
      return;
    }

    try {
      await vendorStore.rejectFinanceVendorRequest(vendorId, rejectionReason, userStore.user?.id || undefined, userStore.user?.name || undefined);
      showSuccess("Request rejected");
      vendorStore.closeFinanceRejectPopup();
      // Refresh the list
      setIsLoading(true);
      vendorStore
        .getVendorRequest(filters)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
        "Something went wrong! Please try again later."
      );
      vendorStore.closeFinanceRejectPopup();
    }
  };

  const handleSourcingApproveConfirm = async (vendorId: number) => {
    if (!userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_SOURCING])) {
      showError("Error", "You are not authorized to approve vendor requests");
      vendorStore.closeSourcingApprovePopup();
      return;
    }

    try {
      await vendorStore.approveSourcingVendorRequest(vendorId, userStore.user?.id || undefined, userStore.user?.name || undefined);
      showSuccess("Sourcing approval successful");
      vendorStore.closeSourcingApprovePopup();
      // Refresh the list
      setIsLoading(true);
      vendorStore
        .getVendorRequest(filters)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
        "Something went wrong! Please try again later."
      );
      vendorStore.closeSourcingApprovePopup();
    }
  };

  const handleSourcingRejectConfirm = async (
    vendorId: number,
    rejectionReason?: string
  ) => {
    if (!userStore.hasPermission([EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_SOURCING])) {
      showError("Error", "You are not authorized to reject vendor requests");
      vendorStore.closeSourcingRejectPopup();
      return;
    }

    try {
      await vendorStore.rejectSourcingVendorRequest(vendorId, rejectionReason, userStore.user?.id || undefined, userStore.user?.name || undefined);
      showSuccess("Sourcing rejection successful");
      vendorStore.closeSourcingRejectPopup();
      // Refresh the list
      setIsLoading(true);
      vendorStore
        .getVendorRequest(filters)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
        "Something went wrong! Please try again later."
      );
      vendorStore.closeSourcingRejectPopup();
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      {!userStore.hasPermission([EmployeeRoles.VIEW_VENDOR_REQUESTS]) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Paper
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
              }}
            >
              <CustomTypography variant="heading3">
                Vendor Requests
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View Vendor Requests
              </CustomTypography>
            </Box>
          </Paper>
          <PermissionBlockLabel />
        </Box>
      )}
      {userStore.hasPermission([EmployeeRoles.VIEW_VENDOR_REQUESTS]) && (
        <Paper
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <CustomTypography variant="heading3">
                Vendor Requests
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View Vendor Requests
              </CustomTypography>
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isFiltersExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "wrap",
                    marginBottom: { xs: 4, md: 0 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: "200px" }}>
                    <FormTextField
                      id="vendor-search"
                      placeholder="Search by Vendor ID, Vendor Name and Email"
                      type="search"
                      variant="outlined"
                      value={searchTerm}
                      onChange={(_, value) => handleSearchBar(value)}
                      onKeyDown={(e) => handleKeyDown(e)}
                    />
                  </Box>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear All"
                    onClick={() => {
                      resetSearch();
                      resetFilters();
                    }}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, }}>
                  <Box sx={{ flex: 1 }}>
                    {renderFilters()}
                  </Box>
                  <Box>
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApplyFilters}
                      customStyles={{ minWidth: 140, height: 37, minHeight: 37 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              {vendorStore.vendorRequests?.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="10rem"
                  textAlign="center"
                  border="1px dashed"
                  borderColor="divider"
                  borderRadius={2}
                >
                  <CustomTypography
                    variant="heading6"
                    fontWeight={500}
                    color={theme.palette.customColors?.greyTextColor}
                  >
                    No vendor requests match the current search criteria.
                  </CustomTypography>
                </Box>
              )}
              {vendorStore.vendorRequests?.length > 0 && (
                <VendorRequestTable
                  maxHeight={window.innerHeight - 350}
                  vendors={vendorStore.vendorRequests || []}
                  limit={filters.limit || 30}
                  page_no={filters.page_no}
                  total_count={vendorStore.total_count}
                  total_pages={vendorStore.total_pages}
                  sortBy={filters.sortBy}
                  sortType={filters.sortType}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column: string, sortType: SortType) => {
                    setFilters((prev: GetVendorRequestsParams) => ({
                      ...prev,
                      sortBy: column,
                      sortType: sortType,
                    }));
                  }}
                  onFinanceApprove={handleFinanceApprove}
                  onFinanceReject={handleFinanceReject}
                  onSourcingApprove={handleSourcingApprove}
                  onSourcingReject={handleSourcingReject}
                  onViewDetails={handleViewDetails}
                />
              )}
            </Box>
          )}

          <VendorDetailsPopup open={vendorStore.viewDetailsPopup.open}
            onClose={vendorStore.closeViewDetailsPopup}
            vendor={getSelectedVendor()}
          />

          {/* <ConfirmationPopup
            id={vendorStore.approvePopup.vendorId || 0}
            open={vendorStore.approvePopup.open}
            title="Approve Vendor Request"
            content="Are you sure you want to approve this vendor request?"
            type="standard"
            onConfirm={handleApproveConfirm}
            onCancel={vendorStore.closeApprovePopup}
          />

          <ConfirmationPopup
            id={vendorStore.rejectPopup.vendorId || 0}
            open={vendorStore.rejectPopup.open}
            title="Reject Vendor Request"
            content="Are you sure you want to reject this vendor request? Please provide a reason for rejection."
            type="form"
            inputRequired={true}
            inputLabel="Rejection Reason"
            inputType="text"
            onConfirm={handleRejectConfirm}
            onCancel={vendorStore.closeRejectPopup}
          /> */}

          <ConfirmationPopup
            id={vendorStore.financeApprovePopup.vendorId || 0}
            open={vendorStore.financeApprovePopup.open}
            title="Finance Approval"
            content="Are you sure you want to approve this vendor request for finance?"
            type="standard"
            onConfirm={handleFinanceApproveConfirm}
            onCancel={vendorStore.closeFinanceApprovePopup}
          />

          <ConfirmationPopup
            id={vendorStore.financeRejectPopup.vendorId || 0}
            open={vendorStore.financeRejectPopup.open}
            title="Finance Rejection"
            content="Are you sure you want to reject this vendor request for finance? Please provide a reason for rejection."
            type="form"
            inputRequired={true}
            inputLabel="Rejection Reason"
            inputType="text"
            onConfirm={handleFinanceRejectConfirm}
            onCancel={vendorStore.closeFinanceRejectPopup}
          />

          <ConfirmationPopup
            id={vendorStore.sourcingApprovePopup.vendorId || 0}
            open={vendorStore.sourcingApprovePopup.open}
            title="Sourcing Approval"
            content="Are you sure you want to approve this vendor request for sourcing?"
            type="standard"
            onConfirm={handleSourcingApproveConfirm}
            onCancel={vendorStore.closeSourcingApprovePopup}
          />

          <ConfirmationPopup
            id={vendorStore.sourcingRejectPopup.vendorId || 0}
            open={vendorStore.sourcingRejectPopup.open}
            title="Sourcing Rejection"
            content="Are you sure you want to reject this vendor request for sourcing? Please provide a reason for rejection."
            type="form"
            inputRequired={true}
            inputLabel="Rejection Reason"
            inputType="text"
            onConfirm={handleSourcingRejectConfirm}
            onCancel={vendorStore.closeSourcingRejectPopup}
          />

        </Paper>
      )}
    </Grid2>
  );
};

export default observer(VendorRequestListing);
